import { tokenStore } from '@/services/api/token'

export interface NotificationMessage {
  uuid: string
  title: string
  content: string
}

type MessageHandler = (messages: NotificationMessage[]) => void
type StatusHandler = (status: WsStatus) => void

export type WsStatus = 'idle' | 'connecting' | 'open' | 'closed' | 'error'

function resolveWsUrl(): string {
  const explicit = import.meta.env.VITE_WS_URL
  if (explicit) return explicit

  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${proto}//${window.location.host}/ws`
}

/**
 * Reconnecting WebSocket manager for the `notifications` channel.
 *
 * Wire format (server → client):
 *   { channel: "notifications", payload: NotificationMessage[] }
 * or a bare NotificationMessage[] array.
 */
class WebSocketManager {
  private socket: WebSocket | null = null
  private status: WsStatus = 'idle'
  private intentionalClose = false
  private retryAttempt = 0
  private retryTimer: ReturnType<typeof setTimeout> | null = null
  private readonly messageHandlers = new Set<MessageHandler>()
  private readonly statusHandlers = new Set<StatusHandler>()

  connect(): void {
    if (
      this.socket &&
      (this.socket.readyState === WebSocket.OPEN ||
        this.socket.readyState === WebSocket.CONNECTING)
    ) {
      return
    }

    this.intentionalClose = false
    this.setStatus('connecting')

    const token = tokenStore.getAccessToken()
    const url = new URL(resolveWsUrl())
    if (token) url.searchParams.set('access_token', token)

    const socket = new WebSocket(url.toString())
    this.socket = socket

    socket.addEventListener('open', () => {
      this.retryAttempt = 0
      this.setStatus('open')
      socket.send(
        JSON.stringify({ action: 'subscribe', channel: 'notifications' }),
      )
    })

    socket.addEventListener('message', (event) => {
      this.handleRaw(event.data)
    })

    socket.addEventListener('error', () => {
      this.setStatus('error')
    })

    socket.addEventListener('close', () => {
      this.socket = null
      this.setStatus('closed')
      if (!this.intentionalClose) {
        this.scheduleReconnect()
      }
    })
  }

  disconnect(): void {
    this.intentionalClose = true
    if (this.retryTimer) {
      clearTimeout(this.retryTimer)
      this.retryTimer = null
    }
    this.socket?.close()
    this.socket = null
    this.setStatus('closed')
  }

  reset(): void {
    this.disconnect()
    this.retryAttempt = 0
  }

  onMessage(handler: MessageHandler): () => void {
    this.messageHandlers.add(handler)
    return () => this.messageHandlers.delete(handler)
  }

  onStatus(handler: StatusHandler): () => void {
    this.statusHandlers.add(handler)
    handler(this.status)
    return () => this.statusHandlers.delete(handler)
  }

  getStatus(): WsStatus {
    return this.status
  }

  private setStatus(status: WsStatus): void {
    this.status = status
    this.statusHandlers.forEach((h) => h(status))
  }

  private scheduleReconnect(): void {
    const delay = Math.min(30_000, 1000 * 2 ** this.retryAttempt)
    this.retryAttempt += 1
    this.retryTimer = setTimeout(() => this.connect(), delay)
  }

  private handleRaw(raw: unknown): void {
    let data: unknown = raw
    if (typeof raw === 'string') {
      try {
        data = JSON.parse(raw)
      } catch {
        return
      }
    }

    const messages = normalizeNotifications(data)
    if (!messages.length) return
    this.messageHandlers.forEach((h) => h(messages))
  }
}

function normalizeNotifications(data: unknown): NotificationMessage[] {
  if (Array.isArray(data)) {
    return data.filter(isNotificationMessage)
  }

  if (
    data &&
    typeof data === 'object' &&
    'payload' in data &&
    Array.isArray((data as { payload: unknown }).payload)
  ) {
    return (data as { payload: unknown[] }).payload.filter(
      isNotificationMessage,
    )
  }

  if (
    data &&
    typeof data === 'object' &&
    'channel' in data &&
    (data as { channel: string }).channel === 'notifications' &&
    'data' in data &&
    Array.isArray((data as { data: unknown }).data)
  ) {
    return (data as { data: unknown[] }).data.filter(isNotificationMessage)
  }

  return []
}

function isNotificationMessage(value: unknown): value is NotificationMessage {
  if (!value || typeof value !== 'object') return false
  const v = value as Record<string, unknown>
  return (
    typeof v.uuid === 'string' &&
    typeof v.title === 'string' &&
    typeof v.content === 'string'
  )
}

export const wsManager = new WebSocketManager()
